import { z } from "zod";

export const SETUP_CHECKLIST_STEP_KEYS = [
  "create_site",
  "add_workers",
  "add_equipment",
  "configure_pay_rules",
  "start_pay_period",
  "submit_field_entry",
  "review_payroll",
] as const;

export type SetupChecklistStepKey = (typeof SETUP_CHECKLIST_STEP_KEYS)[number];

export const setupChecklistStepSchema = z.object({
  key: z.enum(SETUP_CHECKLIST_STEP_KEYS),
  title: z.string(),
  description: z.string(),
  ctaLabel: z.string(),
  href: z.string(),
  completed: z.boolean(),
});

export type SetupChecklistStep = z.infer<typeof setupChecklistStepSchema>;

export const workspaceSetupStatusSchema = z.object({
  steps: z.array(setupChecklistStepSchema),
  completedCount: z.number().int().nonnegative(),
  totalSteps: z.number().int().positive(),
  progressPercent: z.number().int().min(0).max(100),
  nextStepKey: z.enum(SETUP_CHECKLIST_STEP_KEYS).nullable(),
  betaSetupPath: z.enum(["blank", "import_csv", "add_manual"]).nullable(),
  reviewPayPeriodId: z.string().uuid().nullable(),
});

export type WorkspaceSetupStatus = z.infer<typeof workspaceSetupStatusSchema>;

export const WORKSPACE_SETUP_PHASES = ["empty", "partial", "complete"] as const;
export type WorkspaceSetupPhase = (typeof WORKSPACE_SETUP_PHASES)[number];

export const workspaceSetupProgressSchema = workspaceSetupStatusSchema.extend({
  workspaceType: z.enum(["DEMO", "BETA", "PRODUCTION"]),
  phase: z.enum(WORKSPACE_SETUP_PHASES),
  hasRealData: z.boolean(),
});

export type WorkspaceSetupProgress = z.infer<typeof workspaceSetupProgressSchema>;

export function resolveWorkspaceSetupPhase(input: {
  completedCount: number;
  totalSteps: number;
}): WorkspaceSetupPhase {
  if (input.completedCount === 0) return "empty";
  if (input.completedCount >= input.totalSteps) return "complete";
  return "partial";
}

export function hasWorkspaceRealData(status: Pick<WorkspaceSetupStatus, "completedCount">): boolean {
  return status.completedCount > 0;
}

export const SETUP_CHECKLIST_COPY: Record<
  SetupChecklistStepKey,
  { title: string; description: string; ctaLabel: string }
> = {
  create_site: {
    title: "Create your first job site",
    description: "Add the project location where labor and equipment will be tracked.",
    ctaLabel: "Create Site",
  },
  add_workers: {
    title: "Add or import workers",
    description: "Add employee IDs, names, trades, crews, and base rates.",
    ctaLabel: "Add Workers",
  },
  add_equipment: {
    title: "Add equipment",
    description: "Add machines, vehicles, tools, or rentals used on site.",
    ctaLabel: "Add Equipment",
  },
  configure_pay_rules: {
    title: "Configure pay rules",
    description: "Set overtime, double time, weekend, holiday, and premium rules.",
    ctaLabel: "Set Pay Rules",
  },
  start_pay_period: {
    title: "Start first pay period",
    description: "Open the pay period where field entries will be collected.",
    ctaLabel: "Start Pay Period",
  },
  submit_field_entry: {
    title: "Submit first field entry",
    description: "Enter daily labor, equipment usage, job codes, and notes.",
    ctaLabel: "Field Entry",
  },
  review_payroll: {
    title: "Review and export payroll",
    description: "Review calculated hours, exceptions, equipment costs, and exports.",
    ctaLabel: "Payroll Review",
  },
};

export function buildSetupChecklistHref(
  key: SetupChecklistStepKey,
  options?: {
    betaSetupPath?: "blank" | "import_csv" | "add_manual" | null;
    reviewPayPeriodId?: string | null;
  },
): string {
  switch (key) {
    case "create_site":
      return "/sites/new";
    case "add_workers":
      if (options?.betaSetupPath === "import_csv") return "/workers/import";
      return "/workers/new";
    case "add_equipment":
      return "/equipment/new";
    case "configure_pay_rules":
      return "/pay-rules";
    case "start_pay_period":
      return "/pay-periods/new";
    case "submit_field_entry":
      return "/daily-entry";
    case "review_payroll":
      if (options?.reviewPayPeriodId) {
        return `/pay-periods/${options.reviewPayPeriodId}/review`;
      }
      return "/pay-periods";
  }
}

export function isWorkspaceSetupComplete(
  status: Pick<WorkspaceSetupStatus, "completedCount" | "totalSteps">,
): boolean {
  return resolveWorkspaceSetupPhase(status) === "complete";
}
